'use client';

import { useEffect, useRef, useState } from 'react';
import MatrixCanvas from './MatrixCanvas';
import TypingEffect from './TypingEffect';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { trackCtaClick } from '@/lib/analytics';

const STATS = [
  { label: 'PROJECTS_DEPLOYED', value: '5', suffix: '+' },
  { label: 'YEARS_ONLINE',      value: '2',  suffix: '' },
  { label: 'COMMITS_2025',      value: '110', suffix: '' },
  { label: 'UPTIME',            value: '99.9', suffix: '%' },
];

const STATUS_LINES = [
  '> INITIALIZING PORTFOLIO_CORE v2.6.0...',
  '> LOADING SKILL_MODULES...',
  '> ESTABLISHING SECURE_LINK... OK',
  '> PORTFOLIO ONLINE. WELCOME, OPERATOR.',
];
const NAV_ITEMS = ['SKILLS', 'PROJECTS', 'EXPERIENCE', 'EDUCATION', 'ACHIEVEMENTS', 'CONTACT'];

export default function HeroSection() {
  const [visibleLines, setVisibleLines] = useState<number>(0);
  const [scrolled, setScrolled]         = useState(false);
  const [isBooting, setIsBooting]       = useState(false);
  const [hasCheckedBoot, setHasCheckedBoot] = useState(false);
  const [skillLoadProgress, setSkillLoadProgress] = useState(0);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  // Boot sequence
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasSeenBoot = window.sessionStorage.getItem('hero_boot_seen') === '1';
    if (hasSeenBoot) {
      setVisibleLines(STATUS_LINES.length);
      setSkillLoadProgress(100);
      setIsBooting(false);
      setHasCheckedBoot(true);
      return;
    }

    window.sessionStorage.setItem('hero_boot_seen', '1');
    setIsBooting(true);
    setHasCheckedBoot(true);

    const timers: ReturnType<typeof setTimeout>[] = [];
    let progressTimer: ReturnType<typeof setInterval> | null = null;

    timers.push(setTimeout(() => setVisibleLines(1), 600));
    timers.push(
      setTimeout(() => {
        setVisibleLines(2);
        setSkillLoadProgress(0);

        progressTimer = setInterval(() => {
          setSkillLoadProgress((prev) => {
            if (prev >= 100) {
              if (progressTimer) clearInterval(progressTimer);
              return 100;
            }
            return Math.min(prev + 2, 100);
          });
        }, 40);
      }, 1300)
    );
    timers.push(setTimeout(() => setVisibleLines(3), 3600));
    timers.push(setTimeout(() => setVisibleLines(4), 4300));
    timers.push(setTimeout(() => setIsBooting(false), 5600));

    return () => {
      timers.forEach(clearTimeout);
      if (progressTimer) clearInterval(progressTimer);
    };
  }, []);

  // Parallax on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col overflow-hidden"
      style={{ background: 'var(--cyber-black)' }}
      aria-label="Hero section"
    >
      {!hasCheckedBoot && (
        <div className="fixed inset-0 z-[100]" style={{ background: 'var(--cyber-black)' }} />
      )}

      {hasCheckedBoot && isBooting && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-6"
          style={{ background: 'var(--cyber-black)' }}
        >
          <div
            className="terminal-win p-4 max-w-2xl w-full"
            style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)' }}
          >
            <div className="flex items-center gap-2 mb-3 border-b pb-2" style={{ borderColor: 'rgba(0,212,255,0.1)' }}>
              <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--cyber-green)' }} />
              <span className="font-mono text-xs md:text-sm ml-2" style={{ color: 'rgba(200,216,232,0.4)', letterSpacing: '0.15em' }}>
                NIRANJAN_PORTFOLIO.SH - BOOT
              </span>
            </div>
            <div className="space-y-1.5 text-left">
              {STATUS_LINES.slice(0, visibleLines).map((line, i) => (
                <div key={i}>
                  <p
                    className="font-mono text-sm md:text-base"
                    style={{ color: i === visibleLines - 1 ? 'var(--cyber-cyan)' : 'rgba(200,216,232,0.5)' }}
                  >
                    {i === 1 ? `${line} ${skillLoadProgress}%` : line}
                  </p>
                  {i === 1 && (
                    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-sm" style={{ background: 'rgba(0,212,255,0.14)' }}>
                      <div
                        className="h-full transition-[width] duration-75"
                        style={{
                          width: `${skillLoadProgress}%`,
                          background: 'linear-gradient(90deg, var(--cyber-cyan), var(--cyber-magenta))',
                          boxShadow: '0 0 6px rgba(0,212,255,0.6)',
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={!hasCheckedBoot || isBooting ? 'opacity-0 pointer-events-none' : 'opacity-100'}>
        {/* Matrix background */}
        <div className="absolute inset-0 cyber-grid" style={{ backgroundSize: '40px 40px' }} />
        <MatrixCanvas />

      {/* Radial glow center */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(0,212,255,0.06) 0%, transparent 70%)',
        }}
      />

      {/* Diagonal accent line */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '20%', left: '-5%', width: '110%', height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(0,212,255,0.3) 30%, rgba(255,0,110,0.3) 70%, transparent)',
          transform: 'rotate(-3deg)',
        }}
      />

      {/* ---- NAVIGATION ---- */}
      <nav className="relative z-20 flex items-center justify-between px-6 md:px-12 py-6">
        <AppLogo size={72} />
        <div className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <a
              key={item}

              href={`#${item.toLowerCase()}`}
              className="font-mono text-xs tracking-widest transition-all duration-200 hover:text-glow-cyan"
              style={{ color: 'rgba(200,216,232,0.6)', letterSpacing: '0.15em' }}
            >
              {item}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <a
            href="#contact"
            onClick={() => trackCtaClick('hire_me_nav', 'hero_nav', '#contact')}
            className="hidden sm:inline-flex items-center gap-2 font-mono text-[10px] px-3 py-1.5 transition-all duration-200"
            style={{
              color: 'var(--cyber-black)',
              border: '1px solid rgba(0,212,255,0.45)',
              background: 'var(--cyber-cyan)',
              letterSpacing: '0.14em',
              clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
              boxShadow: '0 0 10px rgba(0,212,255,0.3)',
            }}
            aria-label="Hire Niranjan Langade"
          >
            <Icon name="BoltIcon" size={14} />
            HIRE_ME
          </a>
          <a
            href="/Resume.pdf"
            download
            className="hidden sm:inline-flex items-center gap-2 font-mono text-[10px] px-3 py-1.5 transition-all duration-200 hover:opacity-90"
            style={{
              color: 'var(--cyber-cyan)',
              border: '1px solid rgba(0,212,255,0.35)',
              background: 'rgba(0,212,255,0.08)',
              letterSpacing: '0.14em',
              clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
              boxShadow: '0 0 8px rgba(0,212,255,0.22)',
            }}
            aria-label="Download resume"
          >
            <Icon name="ArrowDownTrayIcon" size={14} />
            DOWNLOAD_RESUME
          </a>
          <span className="relative flex h-2 w-2">
            <span className="status-online absolute inline-flex h-full w-full rounded-full" />
          </span>
          <span className="font-mono text-xs text-glow-green hidden sm:block">AVAILABLE_FOR_HIRE</span>
          <button
            type="button"
            onClick={() => setIsMobileNavOpen((prev) => !prev)}
            className="md:hidden inline-flex items-center justify-center w-9 h-9"
            style={{
              color: 'var(--cyber-cyan)',
              border: '1px solid rgba(0,212,255,0.35)',
              background: 'rgba(0,212,255,0.08)',
              clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 6px 100%, 0 calc(100% - 6px))',
            }}
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileNavOpen}
          >
            <Icon name={isMobileNavOpen ? 'XMarkIcon' : 'Bars3Icon'} size={16} />
          </button>
        </div>
      </nav>
      {isMobileNavOpen && (
        <div className="relative z-20 px-6 md:hidden">
          <div
            className="terminal-win p-4 space-y-3"
            style={{
              border: '1px solid rgba(0,212,255,0.2)',
              clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
            }}
          >
            {NAV_ITEMS.map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                onClick={() => setIsMobileNavOpen(false)}
                className="block font-mono text-xs py-2"
                style={{ color: 'rgba(200,216,232,0.8)', letterSpacing: '0.14em' }}
              >
                {item}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => {
                trackCtaClick('hire_me_mobile_nav', 'hero_mobile_nav', '#contact');
                setIsMobileNavOpen(false);
              }}
              className="inline-flex items-center gap-2 font-mono text-[10px] px-3 py-2"
              style={{
                color: 'var(--cyber-black)',
                border: '1px solid rgba(0,212,255,0.45)',
                background: 'var(--cyber-cyan)',
                letterSpacing: '0.14em',
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
                boxShadow: '0 0 10px rgba(0,212,255,0.3)',
              }}
            >
              <Icon name="BoltIcon" size={14} />
              HIRE_ME
            </a>
            <a
              href="/Resume.pdf"
              download
              className="inline-flex items-center gap-2 font-mono text-[10px] px-3 py-2 mt-2"
              style={{
                color: 'var(--cyber-cyan)',
                border: '1px solid rgba(0,212,255,0.35)',
                background: 'rgba(0,212,255,0.08)',
                letterSpacing: '0.14em',
                clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
              }}
            >
              <Icon name="ArrowDownTrayIcon" size={14} />
              DOWNLOAD_RESUME
            </a>
            <div className="flex items-center gap-2 pt-1">
              <span className="relative flex h-2 w-2">
                <span className="status-online absolute inline-flex h-full w-full rounded-full" />
              </span>
              <span className="font-mono text-[10px] text-glow-green">AVAILABLE_FOR_HIRE</span>
            </div>
          </div>
        </div>
      )}

      {/* ---- HERO CONTENT ---- */}
      <div className="relative z-10 flex-1 flex flex-col lg:flex-row items-center gap-12 px-6 md:px-12 py-12 max-w-7xl mx-auto w-full">

        {/* LEFT: Main headline */}
        <div className="flex-1 flex flex-col gap-8">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-3 self-start font-mono text-xs px-4 py-2"
            style={{
              background: 'rgba(0,212,255,0.06)',
              border: '1px solid rgba(0,212,255,0.2)',
              clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))',
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full status-online" />
            <span style={{ color: 'var(--cyber-cyan)', letterSpacing: '0.2em' }}>
              SYS_ID: NIRANJAN_LANGADE // FULL_STACK_DEV
            </span>
          </div>

          {/* Massive headline */}
          <div>
            <p className="font-mono text-sm mb-3" style={{ color: 'var(--cyber-magenta)', letterSpacing: '0.3em' }}>
              // OPERATOR PROFILE
            </p>
            <h1
              className="glitch font-mono font-black leading-none"
              data-text="Niranjan"
              style={{
                fontSize: 'clamp(4rem, 12vw, 9rem)',
                color: '#FFFFFF',
                letterSpacing: '-0.02em',
              }}
            >
              NIRANJAN
            </h1>
            <h1
              className="font-mono font-black leading-none text-glow-cyan"
              style={{
                fontSize: 'clamp(4rem, 12vw, 9rem)',
                letterSpacing: '-0.02em',
              }}
            >
              LANGADE
            </h1>
            <div className="mt-4 font-mono text-lg md:text-2xl" style={{ color: 'rgba(200,216,232,0.7)' }}>
              <TypingEffect
                strings={[
                  'FULL_STACK DEVELOPER',
                  'FREELANCER',
                  'UI/UX ENGINEER',
                  'OPEN_SOURCE CONTRIBUTOR',
                  'SOFTWARE DEVELOPER',
                  'YOUTUBE CREATOR',
                ]}
                speed={70}
                deleteSpeed={40}
              />
            </div>
          </div>

          {/* CTA buttons */}
          <div className="flex flex-wrap gap-4">
            <a
              href="#contact"
              onClick={() => trackCtaClick('hire_me_hero_primary', 'hero_cta', '#contact')}
              className="cyber-btn cyber-btn-primary px-8 py-3 font-mono text-sm font-bold"
            >
              HIRE_ME
            </a>
            <a
              href="#projects"
              onClick={() =>
                trackCtaClick('view_projects_hero_secondary', 'hero_cta', '#projects')
              }
              className="cyber-btn cyber-btn-outline px-8 py-3 font-mono text-sm font-bold"
            >
              VIEW_PROJECTS
            </a>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href="mailto:niranjanlangade@gmail.com"
              onClick={() =>
                trackCtaClick('email_hero_chip', 'hero_quick_contact', 'mailto:niranjanlangade@gmail.com')
              }
              className="font-mono text-[10px] px-3 py-1.5 inline-flex items-center gap-2"
              style={{
                color: 'rgba(200,216,232,0.78)',
                border: '1px solid rgba(0,212,255,0.22)',
                background: 'rgba(0,212,255,0.04)',
                letterSpacing: '0.14em',
              }}
            >
              <Icon name="EnvelopeIcon" size={12} />
              EMAIL
            </a>
            <a
              href="https://www.linkedin.com/in/niranjan45/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackCtaClick('linkedin_hero_chip', 'hero_quick_contact', 'https://www.linkedin.com/in/niranjan45/')
              }
              className="font-mono text-[10px] px-3 py-1.5 inline-flex items-center gap-2"
              style={{
                color: 'rgba(200,216,232,0.78)',
                border: '1px solid rgba(255,0,110,0.25)',
                background: 'rgba(255,0,110,0.06)',
                letterSpacing: '0.14em',
              }}
            >
              <Icon name="UserIcon" size={12} />
              LINKEDIN
            </a>
          </div>
          <a
            href="/about-niranjan-langade"
            className="font-mono text-xs inline-flex items-center gap-2"
            style={{ color: 'rgba(200,216,232,0.7)', letterSpacing: '0.12em' }}
          >
            know more about Niranjan Langade
            <Icon name="ArrowRightIcon" size={13} />
          </a>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="hud-corner relative p-4"
                style={{
                  background: 'rgba(10,15,20,0.8)',
                  border: '1px solid rgba(0,212,255,0.12)',
                }}
              >
                <div className="font-mono font-black text-2xl text-glow-cyan">
                  {s.value}<span style={{ color: 'var(--cyber-magenta)' }}>{s.suffix}</span>
                </div>
                <div className="font-mono text-[9px] mt-1" style={{ color: 'var(--cyber-muted)', letterSpacing: '0.15em' }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: Floating HUD panels */}
        <div className="flex-1 flex justify-center items-center relative min-h-[480px] w-full">

          {/* Central avatar panel */}
          <div
            className="animate-float-slow relative z-10"
            style={{ width: 220, height: 260 }}
          >
            <div
              className="w-full h-full cyber-panel overflow-hidden"
              style={{ border: '1px solid rgba(0,212,255,0.35)' }}
            >
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(0,212,255,0.1) 0%, rgba(255,0,110,0.05) 100%)',
                }}
              />
              {/* Avatar placeholder â€” geometric HUD face */}
              <div className="relative w-full h-full flex flex-col items-center justify-center gap-4 p-4">
                <div
                  className="w-24 h-24 rounded-full relative flex items-center justify-center"
                  style={{
                    border: '2px solid var(--cyber-cyan)',
                    boxShadow: 'var(--glow-cyan)',
                    background: 'rgba(0,212,255,0.08)',
                  }}
                >
                  <svg viewBox="0 0 60 60" className="w-14 h-14" aria-label="Developer avatar silhouette">
                    <circle cx="30" cy="20" r="10" fill="none" stroke="#00D4FF" strokeWidth="1.5"/>
                    <path d="M10 52 Q10 36 30 36 Q50 36 50 52" fill="none" stroke="#00D4FF" strokeWidth="1.5"/>
                    <line x1="20" y1="20" x2="14" y2="14" stroke="#FF006E" strokeWidth="1"/>
                    <line x1="40" y1="20" x2="46" y2="14" stroke="#FF006E" strokeWidth="1"/>
                  </svg>
                  {/* Spinning ring */}
                  <div
                    className="absolute inset-0 rounded-full animate-spin-slow"
                    style={{
                      border: '1px dashed rgba(0,212,255,0.3)',
                      borderTopColor: 'var(--cyber-cyan)',
                    }}
                  />
                </div>
                <div className="text-center">
                  <div className="font-mono text-xs text-glow-cyan font-bold">NIRANJAN.EXE</div>
                  <div className="font-mono text-[9px] mt-1" style={{ color: 'var(--cyber-dim)', letterSpacing: '0.2em' }}>
                    PROCESS_RUNNING
                  </div>
                </div>
                {/* Scan line effect */}
                <div
                  className="absolute left-0 right-0 h-px"
                  style={{
                    background: 'linear-gradient(90deg, transparent, var(--cyber-cyan), transparent)',
                    animation: 'scanLine 3s ease-in-out infinite',
                    top: '50%',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Floating panel â€” top right: Skills preview */}
          <div
            className="absolute animate-float"
            style={{ top: '5%', right: '2%', width: 170 }}
          >
            <div
              className="cyber-panel cyber-panel-sm p-3"
              style={{ border: '1px solid rgba(57,255,20,0.25)' }}
            >
              <div className="font-mono text-[9px] mb-2 text-glow-green" style={{ letterSpacing: '0.2em' }}>
                SKILL_MATRIX
              </div>
              {['React', 'Next.js', 'TypeScript', 'Rust'].map((sk, i) => (
                <div key={sk} className="flex items-center justify-between mb-1.5">
                  <span className="font-mono text-[10px]" style={{ color: 'rgba(200,216,232,0.7)' }}>{sk}</span>
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <div
                        key={j}
                        className="w-2 h-2"
                        style={{
                          background: j < 5 - i * 0.4
                            ? 'var(--cyber-green)'
                            : 'rgba(57,255,20,0.15)',
                          boxShadow: j < 5 - i * 0.4 ? '0 0 4px var(--cyber-green)' : 'none',
                        }}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Floating panel â€” bottom left: Status */}
          <div
            className="absolute animate-float-delayed"
            style={{ bottom: '8%', left: '0%', width: 185 }}
          >
            <div
              className="cyber-panel cyber-panel-sm p-3"
              style={{ border: '1px solid rgba(255,0,110,0.25)' }}
            >
              <div className="font-mono text-[9px] mb-2 text-glow-magenta" style={{ letterSpacing: '0.2em' }}>
                SYSTEM_STATUS
              </div>
              <div className="space-y-1.5">
                {[
                  { label: 'CPU_LOAD', pct: 72, color: 'var(--cyber-cyan)' },
                  { label: 'CREATIVITY', pct: 95, color: 'var(--cyber-magenta)' },
                  { label: 'CAFFEINE', pct: 88, color: 'var(--cyber-yellow)' },
                ].map((item) => (
                  <div key={item.label}>
                    <div className="flex justify-between mb-0.5">
                      <span className="font-mono text-[9px]" style={{ color: 'rgba(200,216,232,0.6)' }}>{item.label}</span>
                      <span className="font-mono text-[9px]" style={{ color: item.color }}>{item.pct}%</span>
                    </div>
                    <div className="progress-track">
                      <div className="progress-fill" style={{ width: `${item.pct}%`, background: item.color, boxShadow: `0 0 6px ${item.color}` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Floating panel â€” top left: Location */}
          <div
            className="absolute animate-float-slow"
            style={{ top: '10%', left: '0%', width: 155 }}
          >
            <div
              className="cyber-panel cyber-panel-sm p-3"
              style={{ border: '1px solid rgba(0,212,255,0.2)' }}
            >
              <div className="font-mono text-[9px] mb-2" style={{ color: 'var(--cyber-cyan)', letterSpacing: '0.2em' }}>
                GEO_LOCATION
              </div>
              <div className="font-mono text-xs font-bold" style={{ color: '#FFFFFF' }}>
                PUNE // REMOTE
              </div>
              <div className="font-mono text-[9px] mt-1" style={{ color: 'rgba(200,216,232,0.5)' }}>
                UTC+5:30 // ONLINE_24H
              </div>
              <div className="mt-2 font-mono text-[9px] text-glow-green">
                ◉ ACCEPTING_WORK
              </div>
            </div>
          </div>

          {/* Decorative orbit ring */}
          <div
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 320, height: 320,
              border: '1px solid rgba(0,212,255,0.08)',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
          <div
            className="absolute rounded-full pointer-events-none animate-spin-slow"
            style={{
              width: 380, height: 380,
              border: '1px dashed rgba(255,0,110,0.08)',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        </div>
      </div>


      {/* Scroll indicator */}
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        style={{ opacity: scrolled ? 0 : 1, transition: 'opacity 0.3s ease' }}
        aria-hidden="true"
      >
        <span className="font-mono text-[9px] tracking-widest" style={{ color: 'rgba(0,212,255,0.5)', letterSpacing: '0.3em' }}>
          SCROLL_DOWN
        </span>
        <div
          className="w-px h-10 animate-pulse"
          style={{ background: 'linear-gradient(180deg, var(--cyber-cyan), transparent)' }}
        />
      </div>

        <style jsx>{`
          @keyframes scanLine {
            0%, 100% { top: 20%; opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            50% { top: 80%; }
          }
        `}</style>
      </div>
    </section>
  );
}
